import {
  bindingSignature,
  type ShortcutActionId,
  type ShortcutBinding,
  type ShortcutBindings,
  SHORTCUT_ACTION_IDS,
} from './bindings.ts';

/** Map KeyA–KeyZ → a–z */
export function codeToLetter(code: string | undefined): string | undefined {
  if (!code || !/^Key[A-Z]$/.test(code)) return undefined;
  return code.slice(3).toLowerCase();
}

/** Prefer event.code; fall back to a single-letter event.key (harness / older paths). */
export function eventLetter(
  event: Pick<KeyboardEvent, 'code' | 'key'>,
): string | undefined {
  const fromCode = codeToLetter(event.code);
  if (fromCode) return fromCode;
  if (typeof event.key === 'string' && /^[a-zA-Z]$/.test(event.key)) {
    return event.key.toLowerCase();
  }
  return undefined;
}

export function matchShortcut(
  event: Pick<KeyboardEvent, 'altKey' | 'ctrlKey' | 'shiftKey' | 'metaKey' | 'code' | 'key'>,
  binding: ShortcutBinding,
): boolean {
  const letter = eventLetter(event);
  if (!letter || letter !== binding.key) return false;

  const wantAlt = binding.modifier === 'alt';
  const wantCtrl = binding.modifier === 'ctrl';
  const wantShift = binding.modifier === 'shift';

  // Exact single-modifier (or none): reject extra modifiers / meta.
  if (Boolean(event.altKey) !== wantAlt) return false;
  if (Boolean(event.ctrlKey) !== wantCtrl) return false;
  if (Boolean(event.shiftKey) !== wantShift) return false;
  if (event.metaKey) return false;
  return true;
}

export function formatShortcut(binding: ShortcutBinding): string {
  const key = binding.key.toUpperCase();
  if (binding.modifier === 'none') return key;
  const label = binding.modifier === 'alt'
    ? 'Alt'
    : binding.modifier === 'ctrl'
      ? 'Ctrl'
      : 'Shift';
  return `${label}+${key}`;
}

export type ShortcutConflict = {
  a: ShortcutActionId;
  b: ShortcutActionId;
  signature: string;
};

export function findBindingConflicts(bindings: ShortcutBindings): ShortcutConflict[] {
  const bySig = new Map<string, ShortcutActionId[]>();
  for (const id of SHORTCUT_ACTION_IDS) {
    const sig = bindingSignature(bindings[id]);
    const list = bySig.get(sig) ?? [];
    list.push(id);
    bySig.set(sig, list);
  }
  const conflicts: ShortcutConflict[] = [];
  for (const [signature, ids] of bySig) {
    if (ids.length < 2) continue;
    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        conflicts.push({ a: ids[i]!, b: ids[j]!, signature });
      }
    }
  }
  return conflicts;
}

export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
